import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';

/**
 * Single place to read values the app keeps in the browser's localStorage.
 *
 * Deliberately small: read-only JSON access with a safe fallback. New code should
 * read session values through here; existing direct localStorage reads elsewhere
 * are unchanged.
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserStorageService {
  constructor(private logger: LoggerService) {}

  /**
   * Parsed JSON stored under `key`, or `fallback` when the key is missing, the
   * storage is unavailable, or the value is not valid JSON (logged as a warning so a
   * corrupt value is traceable instead of failing silently).
   */
  getJson<T>(key: string, fallback: T): T {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(key);
    } catch (err) {
      this.logger.warn(`BrowserStorageService: localStorage unavailable reading "${key}"`, err);
      return fallback;
    }
    if (raw === null || raw === '') {
      return fallback;
    }
    try {
      return JSON.parse(raw) as T;
    } catch (err) {
      this.logger.warn(`BrowserStorageService: "${key}" is not valid JSON`, err);
      return fallback;
    }
  }

  /**
   * Plain string stored under `key` (e.g. `merchant_id`, which is not JSON), or `fallback`
   * when the key is missing/empty, holds the strings "null"/"undefined" (what
   * setItem(key, undefined) writes), or the storage is unavailable.
   */
  getString(key: string, fallback: string | null = null): string | null {
    try {
      const raw = localStorage.getItem(key);
      return raw === null || raw === '' || raw === 'null' || raw === 'undefined' ? fallback : raw;
    } catch (err) {
      this.logger.warn(`BrowserStorageService: localStorage unavailable reading "${key}"`, err);
      return fallback;
    }
  }
}
